const STORAGE_KEY = 'nexus_referral_partner_code';
const COOKIE_MAX_AGE_DAYS = 90;

export function normalizeReferralPartnerCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '');
  return code.length > 0 ? code : null;
}

export function persistReferralPartnerCode(code: string): void {
  const normalized = normalizeReferralPartnerCode(code);
  if (!normalized) return;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(normalized)}; path=/; max-age=${COOKIE_MAX_AGE_DAYS * 86400}; SameSite=Lax`;
  } catch {
    /* ignore storage errors */
  }
}

export function getPersistedReferralPartnerCode(): string | null {
  try {
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage) return normalizeReferralPartnerCode(fromStorage);
    const match = document.cookie.match(new RegExp(`(?:^|; )${STORAGE_KEY}=([^;]*)`));
    if (match?.[1]) return normalizeReferralPartnerCode(decodeURIComponent(match[1]));
  } catch {
    /* ignore */
  }
  return null;
}

export function captureReferralFromSearchParams(searchParams: URLSearchParams): string | null {
  const ref = searchParams.get('ref') || searchParams.get('partner');
  const normalized = normalizeReferralPartnerCode(ref);
  if (normalized) persistReferralPartnerCode(normalized);
  return normalized;
}

export function buildReferralSignupUrl(code: string, siteOrigin: string): string {
  const base = siteOrigin.replace(/\/$/, '');
  return `${base}/signup?ref=${encodeURIComponent(code.trim().toLowerCase())}`;
}

export function buildReferralPartnerInviteUrl(inviteToken: string, siteOrigin: string): string {
  const base = siteOrigin.replace(/\/$/, '');
  return `${base}/referral-partner/invite/${encodeURIComponent(inviteToken)}`;
}

export interface ReferralPartnerInvitePreview {
  ok: boolean;
  error?: 'invalid_invite' | 'inactive' | 'already_claimed' | 'invalid_request';
  partner_name?: string;
  email?: string | null;
  commission_type?: 'one_time' | 'recurring';
  commission_percent?: number;
  code?: string;
  signup_discount_enabled?: boolean;
  signup_discount_percent?: number | null;
}

export interface ReferralPartnerClaimResult {
  ok: boolean;
  error?: 'invalid_invite' | 'inactive' | 'already_claimed' | 'invalid_request';
  partner_name?: string;
  code?: string;
  signer_name?: string;
}

export async function fetchReferralPartnerInvitePreview(
  inviteToken: string,
): Promise<ReferralPartnerInvitePreview> {
  const { supabase } = await import('./supabase');
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data, error } = await supabase.rpc('get_referral_partner_invite_preview', {
    p_token: inviteToken,
  });

  if (error) throw error;
  return (data ?? { ok: false, error: 'invalid_invite' }) as ReferralPartnerInvitePreview;
}

export async function claimReferralPartnerInvite(
  inviteToken: string,
  signerName: string,
): Promise<ReferralPartnerClaimResult> {
  const { supabase } = await import('./supabase');
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data, error } = await supabase.rpc('claim_referral_partner_invite', {
    p_token: inviteToken,
    p_signer_name: signerName.trim(),
  });

  if (error) throw error;
  return (data ?? { ok: false, error: 'invalid_request' }) as ReferralPartnerClaimResult;
}
